import React from "react";

import Dialog from "../../components/dialog/Dialog";
import DialogClose from "../../components/dialog/DialogClose";
import DialogContent from "../../components/dialog/DialogContent";
import DialogFooter from "../../components/dialog/DialogFooter";
import Panel from "../../components/panels/Panel";
import { Font } from "../../types/system/PrintConfiguration";
import DialogOK from "../dialog/DialogOK";

import "./FontMenu.scss";
import Checkbox from "../inputform/Checkbox";
import SelectList from "../inputform/SelectList";

interface FontMenuProps {
    open: boolean;
    font: Font;
    fontsClosed?: Function;
    fontsChanged?: (font: Font) => void;
}

interface FontMenuState {
    isOpen: boolean;
    isLoading: boolean;
    editing: Font;
    fonts: string[];
    sizes: number[];
}

class FontMenu extends React.Component<FontMenuProps, FontMenuState> {
    state = {
        isOpen: false,
        isLoading: false,
        editing: null,
        fonts: [
            "Arial",
            "Helvetica",
            "Times New Roman",
            "Times",
            "Courier",
            "Courier New",
            "Verdana",
            "Georgia",
            "Palatino",
            "Garamond",
            "Bookman",
            "Comic Sans MS",
            "Trebuchet MS",
            "Arial Black",
            "Impact",
        ],
        sizes: [
            8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24,
            25, 26, 27, 28, 29, 30,
        ],
    };

    componentDidMount(): void {}

    componentDidUpdate(
        prevProps: Readonly<FontMenuProps>,
        prevState: Readonly<FontMenuState>,
        snapshot?: any,
    ): void {
        if (this.props.font !== prevProps.font) {
            this.setState(() => ({
                editing: this.props.font,
            }));
        }
    }

    setIsOpen(value: boolean) {
        this.setState(() => ({
            isOpen: value,
        }));
    }

    setIsLoading(value: boolean) {
        this.setState(() => ({
            isLoading: value,
        }));
    }

    setFontName(value: string) {
        this.setState(prevState => ({
            editing: {
                ...prevState.editing,
                name: value,
            },
        }));
    }

    setFontSize(value: number) {
        this.setState(prevState => ({
            editing: {
                ...prevState.editing,
                size: value,
            },
        }));
    }

    setFontWeight(value: number) {
        this.setState(prevState => ({
            editing: {
                ...prevState.editing,
                weight: value,
            },
        }));
    }

    setFontDecoration(value: string) {
        this.setState(prevState => ({
            editing: {
                ...prevState.editing,
                decoration: value,
            },
        }));
    }

    setFont() {
        this.props.fontsChanged?.(this.state.editing);
    }

    render(): React.ReactNode {
        const css = `
            ${this.state.fonts
                .map(
                    item => `
                .${item.replace(/\s/g, "-").toLowerCase()} {
                    font-family: ${item};
                    fontSize: "12pt",
                    height: "unset",
                    padding: "2px",
                    boxSizing: "border-box",
                }
                `,
                )
                .join("\n")}
            .font-size {
                fontSize: "10pt",
                height: "unset",
                padding: "2px",
                boxSizing: "border-box",
            }
        `;
        return (
            <Dialog
                dialogTitle="Font Options"
                showDialog={this.props.open}
                canResize={false}
                width={400}
                zIndex={1000}>
                <DialogContent className="font-dialog">
                    <style>{css}</style>
                    {/* Left panel: Font family selection */}
                    <Panel className="col buffer flex-column">
                        <h3>Font</h3>

                        <SelectList
                            size={9}
                            className="col"
                            value={this.state.editing?.name}
                            options={this.state.fonts.map(item => ({
                                text: item,
                                value: item,
                                className: item
                                    .replace(/\s/g, "-")
                                    .toLowerCase(),
                            }))}
                            onChange={value =>
                                this.setFontName(value)
                            }></SelectList>
                    </Panel>

                    {/* Right panel container: Font size and style */}
                    <Panel className="col-pane buffer flex-column spaced">
                        {/* Top right panel: Font size selection */}
                        <Panel className="col buffer flex-column">
                            <h3>Font Size</h3>

                            <div className="col flex-column">
                                {/* Font size dropdown list (8pt - 30pt) */}
                                <SelectList
                                    size={5}
                                    className="col"
                                    value={this.state.editing?.size}
                                    options={this.state.sizes.map(
                                        (item, index) => ({
                                            text: String(item),
                                            value: item,
                                            order: index,
                                            className: "font-size",
                                        }),
                                    )}
                                    onChange={value =>
                                        this.setFontSize(value)
                                    }></SelectList>
                            </div>
                        </Panel>

                        {/* Bottom right panel: Font style options */}
                        <Panel className="col-pane buffer flex-column">
                            <h3>Font Style</h3>

                            <div className="col flex-column">
                                {/* Bold checkbox - controls font weight (500 = normal, 700 = bold) */}
                                <Checkbox
                                    label="Bold"
                                    value={this.state.editing?.weight === 700}
                                    onChange={value =>
                                        this.setFontWeight(value ? 700 : 500)
                                    }></Checkbox>
                                {/* Italic checkbox - controls font decoration (normal or italic) */}
                                <Checkbox
                                    label="Italic"
                                    value={
                                        this.state.editing?.decoration ===
                                        "italic"
                                    }
                                    onChange={value =>
                                        this.setFontDecoration(
                                            value ? "italic" : "normal",
                                        )
                                    }></Checkbox>
                            </div>
                        </Panel>
                    </Panel>
                </DialogContent>
                <DialogFooter>
                    <DialogClose onClick={this.props.fontsClosed}></DialogClose>
                    <DialogOK onClick={this.setFont.bind(this)}></DialogOK>
                </DialogFooter>
            </Dialog>
        );
    }
}

export default FontMenu;
